import { router } from "@inertiajs/react"
import { AlertCircle, Calendar, Plug } from "lucide-react"
import { type FormEvent, useState } from "react"

import calComIcon from "@/assets/cal_com.svg"
import calendlyIcon from "@/assets/calendly.svg"
import { CapabilityCard } from "@/components/agents/setup/capability-card"
import type {
  CalComEventType,
  CalendarKind,
  CalendlyEventType,
  ConnectedCalendars,
} from "@/components/agents/setup/types"
import {
  type PatCredentials,
  PatCredentialsForm,
} from "@/components/integrations/pat-credentials-form"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import {
  settingsIntegrationCredentialConnectPath,
  settingsMcpConnectPath,
} from "@/routes"

import type { EditableFields } from "../edit-sheet"

type Update = <K extends keyof EditableFields>(
  key: K,
  value: EditableFields[K],
) => void

// IMP-168: only providers that can book a verified slot live on the call.
const CALENDAR_OPTIONS: { id: CalendarKind; label: string; logo: string }[] = [
  { id: "cal_com", label: "Cal.com", logo: calComIcon },
  { id: "calendly", label: "Calendly", logo: calendlyIcon },
]

export function AppointmentsPanel({
  fields,
  onChange,
  connectedCalendars,
  calendlyEventTypes,
  calComEventTypes,
  agentUid,
  eventTypeError,
}: {
  fields: EditableFields
  onChange: Update
  connectedCalendars: ConnectedCalendars
  calendlyEventTypes: CalendlyEventType[]
  calComEventTypes: CalComEventType[]
  agentUid: string
  // IMP-168: server-side "a paid Calendly plan is required" message, shown
  // under the event-type picker when a Free-Calendly save is rejected.
  eventTypeError?: string | null
}) {
  const enabled = fields.appointments_enabled
  // IMP-321: only treat a calendar as selected when it's actually connected.
  const rawKind = fields.appointment_calendar_kind
  const kind = rawKind && connectedCalendars[rawKind] ? rawKind : ""
  const connected = kind !== "" && connectedCalendars[kind]

  // IMP-168: a chosen event type is mandatory, picked from the provider's live
  // list — no free-text fallback.
  const eventTypes =
    kind === "calendly"
      ? calendlyEventTypes.map((et) => ({ value: et.uri, et }))
      : kind === "cal_com"
        ? calComEventTypes.map((et) => ({ value: String(et.id), et }))
        : []
  const hasEventTypes = eventTypes.length > 0

  const [calComDialogOpen, setCalComDialogOpen] = useState(false)

  function connectCalendar(calKind: CalendarKind) {
    if (calKind === "cal_com") {
      setCalComDialogOpen(true)
      return
    }
    const returnTo = `/agents/${agentUid}`
    window.location.assign(
      settingsMcpConnectPath(calKind) +
        `?return_to=${encodeURIComponent(returnTo)}`,
    )
  }

  return (
    <>
      <CapabilityCard
        icon={Calendar}
        on={enabled}
        title="Book appointments"
        description="Offers your open times and books the slot before the conversation ends."
        onToggle={(v) => onChange("appointments_enabled", v)}
      >
        <section className="space-y-2">
          <Label>Calendar integration</Label>
          <div className="grid grid-cols-2 gap-3">
            {CALENDAR_OPTIONS.map(({ id, label, logo }) => {
              const connected = connectedCalendars[id]
              const selected = kind === id
              return (
                <div
                  key={id}
                  onClick={() => {
                    if (connected) onChange("appointment_calendar_kind", id)
                  }}
                  className={cn(
                    "rounded-md border p-3 transition-colors",
                    selected
                      ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
                      : "border-border",
                    connected && "cursor-pointer",
                    connected && !selected && "hover:border-blue-200",
                  )}
                >
                  <div className="mb-4 flex items-start justify-between">
                    <img src={logo} alt={label} className="size-7" />
                    {connected ? (
                      <span className="border-border inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium">
                        Connected
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          connectCalendar(id)
                        }}
                        className="border-border hover:bg-muted flex size-6 items-center justify-center rounded-sm border transition-colors"
                        aria-label={`Connect ${label}`}
                      >
                        <Plug className="text-muted-foreground size-3.5" />
                      </button>
                    )}
                  </div>
                  <p
                    className={cn(
                      "text-sm font-medium",
                      selected && "text-blue-600",
                    )}
                  >
                    {label}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        {/* IMP-168: a chosen event type is mandatory, from the provider's
              live list — no free-text or booking-link fields. */}
        {connected && hasEventTypes && (
          <section className="space-y-2">
            <Label htmlFor="appointment_event_type_uri_panel">Event type</Label>
            <select
              id="appointment_event_type_uri_panel"
              value={fields.appointment_event_type_uri}
              onChange={(e) =>
                onChange("appointment_event_type_uri", e.target.value)
              }
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              <option value="">Choose an event type…</option>
              {eventTypes.map(({ value, et }) => (
                <option key={value} value={value}>
                  {et.name}
                  {et.duration_minutes ? ` (${et.duration_minutes} min)` : ""}
                </option>
              ))}
            </select>
            {eventTypeError && (
              <p className="text-destructive text-xs">{eventTypeError}</p>
            )}
          </section>
        )}

        {connected && !hasEventTypes && (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>No bookable event types</AlertTitle>
            <AlertDescription>
              Create an event type in{" "}
              {kind === "calendly" ? "Calendly" : "cal.com"} first, then refresh
              — the receptionist books against a specific event type.
            </AlertDescription>
          </Alert>
        )}
      </CapabilityCard>

      <CalComConnectDialog
        open={calComDialogOpen}
        onOpenChange={setCalComDialogOpen}
        returnTo={`/agents/${agentUid}`}
        onConnected={() => {
          // Mirror the wizard: enable + select cal_com once the PAT
          // validates so the user lands on the configured state without
          // having to click anything else. setup_data updates via the
          // sheet's Save button.
          onChange("appointments_enabled", true)
          onChange("appointment_calendar_kind", "cal_com")
          setCalComDialogOpen(false)
        }}
      />
    </>
  )
}

// IMP-176: lets users connect Cal.com without leaving the edit drawer.
// Identical shape to the wizard-step version; kept inline rather than
// extracted to a shared component because the two sites import slightly
// different post-success behavior (the wizard uses setState; the drawer
// uses the parent's onChange to flow into editable fields).
function CalComConnectDialog({
  open,
  onOpenChange,
  onConnected,
  returnTo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConnected: () => void
  // See IMP-176 follow-up comment in the wizard's CalComConnectDialog.
  returnTo: string
}) {
  const [credentials, setCredentials] = useState<PatCredentials>({
    personalAccessToken: "",
  })
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const filled = credentials.personalAccessToken.trim() !== ""

  function reset() {
    setCredentials({ personalAccessToken: "" })
    setError(null)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setError(null)
    router.post(
      settingsIntegrationCredentialConnectPath("cal_com"),
      {
        personal_access_token: credentials.personalAccessToken,
        return_to: returnTo,
      },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          reset()
          onConnected()
        },
        onError: () => {
          setError(
            "Unable to verify token. Check that it's correct and try again.",
          )
        },
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Connect Cal.com</DialogTitle>
            <DialogDescription>
              Paste your Cal.com Personal Access Token. We&rsquo;ll use it to
              pull your event types and book appointments on your behalf.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertTitle>Connection failed</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <PatCredentialsForm
              values={credentials}
              onChange={setCredentials}
              idPrefix="cal-com-cred-panel"
              helpText="Generate this in Cal.com → Settings → Developer → API keys."
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!filled || processing}
              loading={processing}
            >
              {processing ? "Connecting..." : "Connect"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
